// src/lib/categories.ts
import { supabase } from '../integrations/supabase/client';
import type { Category, Subcategory, CategoryFormData, SubcategoryFormData } from '../types/category';

// Helper function to check for duplicate category names
async function checkDuplicateCategoryName(name: string, excludeId?: string): Promise<boolean> {
  let query = supabase
    .from('categories')
    .select('id', { count: 'exact', head: true })
    .eq('name', name)
    .eq('deleted', false);

  if (excludeId) {
    query = query.not('id', 'eq', excludeId);
  }

  const { count, error } = await query;

  if (error) {
    console.error("Error checking duplicate category name:", error);
    return true;
  }
  return (count ?? 0) > 0;
}

// Helper function to check for duplicate subcategory names within the same category
async function checkDuplicateSubcategoryName(name: string, categoryId: string, excludeId?: string): Promise<boolean> {
  let query = supabase
    .from('subcategories')
    .select('id', { count: 'exact', head: true })
    .eq('name', name)
    .eq('category_id', categoryId)
    .eq('deleted', false);

  if (excludeId) {
    query = query.not('id', 'eq', excludeId);
  }

  const { count, error } = await query;

  if (error) {
    console.error("Error checking duplicate subcategory name:", error);
    return true;
  }
  return (count ?? 0) > 0;
}


// --- Category Functions ---

export async function getCategories(): Promise<Category[]> {
    // Added image_url to the selection
    const selectString = `
      id, name, description, status, deleted, created_at, updated_at, image_url,
      subcategories (
        id, category_id, name, description, status, deleted, display_order, created_at, updated_at
      )
    `;

  let query = supabase
    .from('categories')
    .select(selectString)
    .eq('deleted', false)
    // Only fetch active categories for the storefront usually
    .eq('status', 'active') 
    .order('name', { ascending: true })
    .order('display_order', { foreignTable: 'subcategories', ascending: true });

  const { data, error } = await query;

   if (error) {
    console.error('Error fetching categories with related data:', error);
    return [];
  }

  // Map data and filter subcategories
    const mappedData = (data || []).map(category => ({
        // Map all fields from Category type definition
        id: category.id,
        name: category.name,
        description: category.description || undefined,
        status: category.status,
        imageUrl: category.image_url || null, // Added imageUrl mapping
        // Filter and map subcategories
        subcategories: (category.subcategories || [])
          .filter((sub: any) => !sub.deleted && sub.status === 'active') // Filter here
          .map((sub: any) => ({
            id: sub.id,
            categoryId: sub.category_id,
            name: sub.name,
            description: sub.description || undefined,
            status: sub.status,
            displayOrder: sub.display_order,
            deleted: sub.deleted,
            createdAt: new Date(sub.created_at),
            updatedAt: new Date(sub.updated_at),
        } as Subcategory)),
        deleted: category.deleted,
        createdAt: new Date(category.created_at),
        updatedAt: new Date(category.updated_at),
    })) as Category[];

    return mappedData;
}

// --- Subcategory Functions ---

export interface SubcategoryWithCategoryName extends Subcategory {
    categoryName: string | null;
}

export async function getSubcategoriesWithCategoryName(): Promise<SubcategoryWithCategoryName[]> {
    const selectString = `
        id, category_id, name, description, status, deleted, display_order, created_at, updated_at,
        categories ( name )
    `;

    let query = supabase
        .from('subcategories')
        .select(selectString)
        .eq('deleted', false)
        .order('display_order', { ascending: true })
        .order('name', { ascending: true });

    const { data, error } = await query;

    if (error) {
        console.error('Error fetching subcategories with category name:', error);
        return [];
    }

    // Map data, extract category name
    return (data || []).map(sub => {
        const categoryName = sub.categories ? (sub.categories as { name: string }).name : null;

        return {
            // Map all fields from Subcategory type definition
            id: sub.id,
            categoryId: sub.category_id,
            name: sub.name,
            description: sub.description || undefined,
            status: sub.status,
            displayOrder: sub.display_order,
            deleted: sub.deleted,
            createdAt: new Date(sub.created_at),
            updatedAt: new Date(sub.updated_at),
            categoryName: categoryName,
        } as SubcategoryWithCategoryName;
    });
}

// --- CRUD Functions --- 

// Ensure CategoryFormData includes image_url if needed for create/update
export async function createCategory(formData: CategoryFormData): Promise<{ data: Category | null, error: string | null }> {
  const isDuplicate = await checkDuplicateCategoryName(formData.name);
  if (isDuplicate) {
    return { data: null, error: 'Category name already exists.' };
  }

  const dataToInsert = {
      ...formData,
      deleted: false,
  }

  const { data, error } = await supabase
    .from('categories')
    .insert([dataToInsert])
    .select('*, image_url') // Ensure image_url is selected back
    .single();

  if (error) {
    console.error('Error creating category:', error);
    return { data: null, error: error.message };
  }
    const result = data ? { 
        id: data.id,
        name: data.name,
        description: data.description || undefined,
        status: data.status,
        imageUrl: data.image_url || null, // Map imageUrl
        subcategories: [],
        deleted: data.deleted,
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at)
    } as Category : null;
    return { data: result, error: null };
}

export async function updateCategory(id: string, formData: Partial<CategoryFormData>): Promise<{ data: Category | null, error: string | null }> {
  if (formData.name) {
        const isDuplicate = await checkDuplicateCategoryName(formData.name, id);
        if (isDuplicate) {
            return { data: null, error: 'Category name already exists.' };
        }
    }

   const dataToUpdate = {
       ...formData,
       updated_at: new Date()
   }

  const { data, error } = await supabase
    .from('categories')
    .update(dataToUpdate)
    .eq('id', id)
    .select('*, image_url') // Ensure image_url is selected back
    .single();

  if (error) {
    console.error('Error updating category:', error);
    return { data: null, error: error.message };
  }
    const result = data ? { 
        id: data.id,
        name: data.name,
        description: data.description || undefined,
        status: data.status,
        imageUrl: data.image_url || null, // Map imageUrl
        subcategories: [],
        deleted: data.deleted,
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at)
    } as Category : null;
    return { data: result, error: null };
}

export async function deleteCategory(id: string): Promise<{ error: string | null }> {
  // Soft delete category
  const { error: catError } = await supabase
    .from('categories')
    .update({ deleted: true, status: 'inactive', updated_at: new Date() })
    .eq('id', id);

  // Soft delete associated subcategories
  const { error: subError } = await supabase
    .from('subcategories')
    .update({ deleted: true, status: 'inactive', updated_at: new Date() })
    .eq('category_id', id);

  if (catError || subError) {
    console.error('Error soft deleting category or its subcategories:', catError || subError);
    const message = catError?.message || subError?.message || 'Failed to delete category and/or subcategories';
    return { error: message };
  }
  return { error: null };
}


export async function createSubcategory(formData: SubcategoryFormData): Promise<{ data: Subcategory | null, error: string | null }> {
   const isDuplicate = await checkDuplicateSubcategoryName(formData.name, formData.categoryId);
    if (isDuplicate) {
        return { data: null, error: 'Subcategory name already exists in this category.' };
    }

    const dbData = {
        name: formData.name,
        description: formData.description,
        status: formData.status,
        display_order: formData.displayOrder,
        category_id: formData.categoryId,
        deleted: false,
    };

  const { data, error } = await supabase
    .from('subcategories')
    .insert([dbData])
    .select()
    .single();

  if (error) {
    console.error('Error creating subcategory:', error);
    return { data: null, error: error.message };
  }
    const result = data ? {
        id: data.id,
        categoryId: data.category_id,
        name: data.name,
        description: data.description || undefined,
        status: data.status,
        displayOrder: data.display_order,
        deleted: data.deleted,
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at),
    } as Subcategory : null;
    return { data: result, error: null };
}

export async function updateSubcategory(id: string, formData: Partial<SubcategoryFormData>): Promise<{ data: Subcategory | null, error: string | null }> {

    const dbData: { [key: string]: any } = { updated_at: new Date() };
    if (formData.name !== undefined) dbData.name = formData.name;
    if (formData.description !== undefined) dbData.description = formData.description;
    if (formData.status !== undefined) dbData.status = formData.status;
    if (formData.categoryId !== undefined) dbData.category_id = formData.categoryId;
    if (formData.displayOrder !== undefined) dbData.display_order = formData.displayOrder;

    let categoryIdToCheck = formData.categoryId;
    if (formData.name && !formData.categoryId) {
        const { data: currentSub, error: fetchError } = await supabase
            .from('subcategories')
            .select('category_id')
            .eq('id', id)
            .single();
        if (fetchError || !currentSub) {
            console.warn("Could not fetch current categoryId for duplicate check. Skipping check.");
        } else {
            categoryIdToCheck = currentSub.category_id;
        }
    }

    if (formData.name && categoryIdToCheck) {
        const isDuplicate = await checkDuplicateSubcategoryName(formData.name, categoryIdToCheck, id);
        if (isDuplicate) {
            return { data: null, error: 'Subcategory name already exists in this category.' };
        }
    }

  const { data, error } = await supabase
    .from('subcategories')
    .update(dbData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating subcategory:', error);
    return { data: null, error: error.message };
  }
    const result = data ? {
        id: data.id,
        categoryId: data.category_id,
        name: data.name,
        description: data.description || undefined,
        status: data.status,
        displayOrder: data.display_order,
        deleted: data.deleted,
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at),
    } as Subcategory : null;
    return { data: result, error: null };
}

export async function deleteSubcategory(id: string): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from('subcategories')
    .update({ deleted: true, status: 'inactive', updated_at: new Date() })
    .eq('id', id);

  if (error) {
    console.error('Error soft deleting subcategory:', error);
    return { error: error.message };
  }
  return { error: null };
}


// --- NEW FUNCTION ---
/**
 * Fetches only subcategories belonging to a specific category ID.
 */
export async function getSubcategoriesByCategoryId(categoryId: string): Promise<Subcategory[]> {
    if (!categoryId) {
        return []; // Return empty if no categoryId is provided
    }

    const selectString = `
        id, category_id, name, description, status, deleted, display_order, created_at, updated_at
    `;

    let query = supabase
        .from('subcategories')
        .select(selectString)
        .eq('category_id', categoryId)
        .eq('deleted', false) // Only non-deleted
        .eq('status', 'active') // Usually only show active subcategories in the form dropdown
        .order('display_order', { ascending: true })
        .order('name', { ascending: true });

    const { data, error } = await query;

    if (error) {
        console.error(`Error fetching subcategories for category ${categoryId}:`, error);
        return [];
    }

    // Map data using the existing mapping logic
    return (data || []).map(sub => ({
        id: sub.id,
        categoryId: sub.category_id,
        name: sub.name,
        description: sub.description || undefined,
        status: sub.status,
        displayOrder: sub.display_order,
        deleted: sub.deleted,
        createdAt: new Date(sub.created_at),
        updatedAt: new Date(sub.updated_at),
    } as Subcategory));
}
